import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money, moneyWithCurrency } from '../../utils/region';

// Monthly "Summary" view (Monthly Tax → filing selector → Summary): the tax amounts payable for the
// selected month (PND.3 / PND.53 / net VAT PP.30), each with a checkbox. "Generate Email" opens a
// pre-filled, fully-editable Tax Payment Notification composer (TPL-031) that the operator reviews
// before sending to the client. PND.1 and SSO are intentionally excluded (sent in separate emails).

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const validEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

type ItemKey = 'pnd3' | 'pnd53' | 'vat';
const ROWS: { key: ItemKey; label: string }[] = [
  { key: 'pnd3', label: 'PND 3' },
  { key: 'pnd53', label: 'PND 53' },
  { key: 'vat', label: 'VAT (PP 30)' },
];

function EmailChips({ value, onChange, ph }: { value: string[]; onChange: (v: string[]) => void; ph: string }) {
  const [q, setQ] = useState('');
  const add = (raw: string) => { const v = raw.trim().toLowerCase(); if (v && validEmail(v) && !value.includes(v)) onChange([...value, v]); setQ(''); };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '6px 8px', border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)' }}>
      {value.map(e => (
        <span key={e} style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 8, background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {e}<span onClick={() => onChange(value.filter(x => x !== e))} style={{ cursor: 'pointer', fontWeight: 800 }}>×</span>
        </span>
      ))}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={ph}
        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && q.trim()) { e.preventDefault(); add(q); } }}
        onBlur={() => q.trim() && add(q)}
        style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)' }} />
    </div>
  );
}

// ── Composer (mirrors PropertyTaxEmailComposer; no attachments) ──────────────────────────────────
// sel: per-line WHT subset for PND.3/PND.53 (Calem 2026-09-07) — notice mode only; the
// filing-complete pack deliberately stays category-level (it is an archive, not a collection).
function NoticeComposer({ clientId, year, month, items, sel, mode = 'notice', onClose, onSent }: {
  clientId: string; year: number; month: number; items: ItemKey[]; sel?: { pnd3?: string[]; pnd53?: string[] }; mode?: 'notice' | 'pack'; onClose: () => void; onSent?: () => void;
}) {
  const isPack = mode === 'pack';
  const [files, setFiles] = useState<File[]>([]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [initialBody, setInitialBody] = useState('');
  const [warning, setWarning] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    (isPack ? api.prepareFilingPack(clientId, year, month, items) : api.prepareTaxNotice(clientId, year, month, items, sel)).then((d: any) => {
      if (!alive) return;
      setFrom(d.from || ''); setTo(d.to || []); setCc(d.cc || []);
      setSubject(d.subject || ''); setInitialBody(d.bodyHtml || ''); setWarning(d.warning || '');
    }).catch((e: any) => alive && setErr(e?.message || 'Failed to prepare email')).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [clientId, year, month]); // eslint-disable-line

  const send = async () => {
    const body = bodyRef.current ? bodyRef.current.innerHTML : initialBody;
    if (!to.length) { setErr(t('iv_email_need')); return; }
    setBusy(true); setErr('');
    try {
      if (isPack) {
        const fd = new FormData();
        fd.append('from', from); fd.append('to', to.join(',')); fd.append('cc', cc.join(','));
        fd.append('subject', subject); fd.append('bodyHtml', body);
        fd.append('year', String(year)); fd.append('month', String(month));
        files.forEach(f => fd.append('files', f));
        await api.sendFilingPack(clientId, fd);
      } else {
        await api.sendTaxNotice(clientId, { from, to, cc, subject, bodyHtml: body });
      }
      setDone(true); onSent?.(); setTimeout(onClose, 1200); }
    catch (e: any) { setErr(e?.message || 'Failed to send'); } finally { setBusy(false); }
  };

  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 600, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{isPack ? 'Filing-Complete Pack — receipts to the client' : t('tx_notice_email_title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('iv_email_cc')}</div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} /></div>
        ) : (
          <div style={{ marginTop: 14 }}>
            {warning && <div style={{ fontSize: 12.5, color: '#92400E', background: '#FEF3C7', borderRadius: 9, padding: '8px 11px', marginBottom: 12 }}><i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />{warning}</div>}

            <label style={lbl}>{t('bie_from')}</label>
            <input value={from} onChange={e => setFrom(e.target.value)} style={inp} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_to')}</label>
            <EmailChips value={to} onChange={setTo} ph={t('bie_email_ph')} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_cc')}</label>
            <EmailChips value={cc} onChange={setCc} ph={t('bie_email_ph')} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_subject')}</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} style={inp} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_body')}</label>
            <div ref={bodyRef} contentEditable suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: initialBody }}
              style={{ minHeight: 220, maxHeight: 360, overflowY: 'auto', padding: '11px 13px', fontSize: 13.5, lineHeight: 1.6, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none' }} />

            {isPack && (
              <div style={{ marginTop: 13 }}>
                <label style={lbl}>Attachments — filing receipts (PDF/images, up to 10)</label>
                <input type="file" multiple accept="application/pdf,image/*"
                  onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files || [])].slice(0, 10))}
                  style={{ fontSize: 12.5, color: 'var(--text-secondary)' }} />
                {files.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-primary)', marginTop: 5 }}>
                    <i className="ti ti-paperclip" />{f.name} <span style={{ color: 'var(--text-tertiary)' }}>({(f.size / 1048576).toFixed(1)} MB)</span>
                    <span onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} style={{ cursor: 'pointer', color: '#E24B4A', fontWeight: 800 }}>×</span>
                  </div>
                ))}
                {!files.length && <div style={{ fontSize: 12, color: '#92400E', marginTop: 5 }}>No receipts attached yet — the email states documents are attached.</div>}
              </div>
            )}
            {err && <div style={{ fontSize: 12.5, color: '#E24B4A', marginTop: 12 }}>{err}</div>}
            {done ? (
              <div style={{ fontSize: 13.5, color: '#15803D', fontWeight: 700, marginTop: 16 }}><i className="ti ti-circle-check" style={{ marginRight: 6 }} />{t('iv_email_sent')}</div>
            ) : (
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
                <button onClick={send} disabled={busy} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.55 : 1 }}>{busy ? t('login_wait') : t('iv_email_send')}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type WhtLine = { id: string; payee: string; amount: number };
type WhtKey = 'pnd3' | 'pnd53';

export default function TaxSummaryPanel({ clientId, clientName, year, month }: { clientId: string; clientName?: string; year: number; month: number }) {
  const { t } = useTranslation();
  const [data, setData] = useState<{ pnd3: number; pnd53: number; vat: number; vatBrought?: number; vatCarry?: number } | null>(null);
  const [details, setDetails] = useState<Record<WhtKey, WhtLine[]>>({ pnd3: [], pnd53: [] });
  const [loading, setLoading] = useState(true);
  // Per-line selection (owner/Calem 2026-09-07): PND.3/PND.53 open into their payee lines, each
  // individually selectable — "send email for collect payment only the third item". VAT is a
  // computed net with no lines, so it keeps its single whole-category checkbox.
  const [sel, setSel] = useState<Record<WhtKey, Set<string>>>({ pnd3: new Set(), pnd53: new Set() });
  const [open, setOpen] = useState<Record<WhtKey, boolean>>({ pnd3: false, pnd53: false });
  const [vatChecked, setVatChecked] = useState(false);
  const [compose, setCompose] = useState(false);
  const [composePack, setComposePack] = useState(false);

  useEffect(() => {
    let alive = true; setLoading(true);
    api.taxSummary(clientId, year, month).then((d: any) => {
      if (!alive) return;
      setData({ pnd3: d.pnd3, pnd53: d.pnd53, vat: d.vat, vatBrought: d.vatBrought, vatCarry: d.vatCarry });
      const det: Record<WhtKey, WhtLine[]> = { pnd3: d.details?.pnd3 || [], pnd53: d.details?.pnd53 || [] };
      setDetails(det);
      // Default-check only categories with a positive payable amount (skip 0 / VAT credits) —
      // a checked category starts with ALL of its lines selected.
      setSel({
        pnd3: new Set(Number(d.pnd3) > 0 ? det.pnd3.map(l => l.id) : []),
        pnd53: new Set(Number(d.pnd53) > 0 ? det.pnd53.map(l => l.id) : []),
      });
      setVatChecked(Number(d.vat) > 0);
      setOpen({ pnd3: false, pnd53: false });
    }).catch(() => alive && setData(null)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [clientId, year, month]);

  const sum2 = (n: number) => Math.round(n * 100) / 100;
  // Category amount = its SELECTED lines (equals the full figure when everything is ticked);
  // VAT has no lines so it is always the computed net payable.
  const amountOf = (k: ItemKey): number => {
    if (k === 'vat') return data ? Number(data.vat) || 0 : 0;
    if (details[k].length) return sum2(details[k].filter(l => sel[k].has(l.id)).reduce((s, l) => s + l.amount, 0));
    return 0;
  };
  const fullOf = (k: WhtKey): number => data ? Number((data as any)[k]) || 0 : 0;
  const catChecked = (k: ItemKey): boolean => k === 'vat' ? vatChecked : sel[k].size > 0;
  const toggleCat = (k: ItemKey, on: boolean) => {
    if (k === 'vat') { setVatChecked(on); return; }
    setSel(s => ({ ...s, [k]: new Set(on ? details[k].map(l => l.id) : []) }));
  };
  const toggleLine = (k: WhtKey, id: string, on: boolean) => {
    setSel(s => { const next = new Set(s[k]); if (on) next.add(id); else next.delete(id); return { ...s, [k]: next }; });
  };
  const pickedItems = ROWS.filter(r => catChecked(r.key)).map(r => r.key);
  const noticeSel = {
    pnd3: sel.pnd3.size ? Array.from(sel.pnd3) : undefined,
    pnd53: sel.pnd53.size ? Array.from(sel.pnd53) : undefined,
  };
  const total = sum2(pickedItems.reduce((s, k) => s + amountOf(k), 0));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} /></div>;

  const cellL: React.CSSProperties = { padding: '12px 14px', borderBottom: '1px solid var(--border)' };
  const cellR: React.CSSProperties = { ...cellL, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '8px 12px' }}>
        <i className="ti ti-info-circle" style={{ color: '#15803D' }} />{t('tx_summary_hint')}
      </div>

      <div style={{ maxWidth: 560, border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', background: 'var(--bg-surface)' }}>
        <div style={{ padding: '10px 14px', fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)', background: 'var(--bg-active)', borderBottom: '1px solid var(--border)' }}>
          {t('tx_summary_title')} · {MONTHS[month - 1]} {year}{clientName ? ` · ${clientName}` : ''}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <tbody>
            {ROWS.map(r => {
              const isWht = r.key !== 'vat';
              const lines = isWht ? details[r.key as WhtKey] : [];
              const nSel = isWht ? sel[r.key as WhtKey].size : 0;
              const partial = isWht && nSel > 0 && nSel < lines.length;
              return (
                <React.Fragment key={r.key}>
                  <tr>
                    <td style={{ ...cellL, width: 42 }}>
                      <input type="checkbox" checked={catChecked(r.key)}
                        ref={el => { if (el) el.indeterminate = partial; }}
                        onChange={e => toggleCat(r.key, e.target.checked)} style={{ cursor: 'pointer', width: 16, height: 16 }} />
                    </td>
                    <td style={{ ...cellL, fontWeight: 600, color: 'var(--text-primary)' }}>
                      {lines.length > 0 ? (
                        <span onClick={() => setOpen(o => ({ ...o, [r.key]: !o[r.key as WhtKey] }))} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <i className={`ti ti-chevron-${open[r.key as WhtKey] ? 'down' : 'right'}`} style={{ fontSize: 13, color: 'var(--text-tertiary)' }} aria-hidden />
                          {r.label}
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)' }}>{nSel}/{lines.length}</span>
                        </span>
                      ) : r.label}
                      {partial && (
                        <div style={{ fontSize: 11, fontWeight: 500, color: '#B45309', marginTop: 2 }}>partial — full amount {moneyWithCurrency(fullOf(r.key as WhtKey))}</div>
                      )}
                      {r.key === 'vat' && !!(Number(data?.vatBrought) || Number(data?.vatCarry)) && (
                        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-tertiary)', marginTop: 2 }}>
                          {Number(data?.vatBrought) ? `${t('tx_credit_bf')}: ${money(data?.vatBrought)}` : ''}
                          {Number(data?.vatBrought) && Number(data?.vatCarry) ? ' · ' : ''}
                          {Number(data?.vatCarry) ? `${t('tx_credit_cf')}: ${money(data?.vatCarry)}` : ''}
                        </div>
                      )}</td>
                    <td style={{ ...cellR, color: amountOf(r.key) < 0 ? '#B45309' : 'var(--text-primary)' }}>{moneyWithCurrency(amountOf(r.key))}</td>
                  </tr>
                  {isWht && open[r.key as WhtKey] && lines.map(l => (
                    <tr key={l.id} style={{ background: 'var(--c-gray-50)' }}>
                      <td style={{ ...cellL, width: 42 }} />
                      <td style={{ ...cellL, paddingLeft: 34 }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
                          <input type="checkbox" checked={sel[r.key as WhtKey].has(l.id)}
                            onChange={e => toggleLine(r.key as WhtKey, l.id, e.target.checked)} style={{ cursor: 'pointer', width: 14, height: 14 }} />
                          {l.payee || '—'}
                        </label>
                      </td>
                      <td style={{ ...cellR, fontWeight: 600, fontSize: 13, color: 'var(--text-secondary)' }}>{moneyWithCurrency(l.amount)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            <tr>
              <td style={{ padding: '13px 14px' }} />
              <td style={{ padding: '13px 14px', fontWeight: 800, color: 'var(--text-primary)' }}>{t('tx_summary_total')}</td>
              <td style={{ padding: '13px 14px', textAlign: 'right' }}>
                <span style={{ display: 'inline-block', background: '#111827', color: '#fff', fontWeight: 800, fontSize: 14, padding: '5px 14px', borderRadius: 8, whiteSpace: 'nowrap' }}>{moneyWithCurrency(total)}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ maxWidth: 560, display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button onClick={() => setCompose(true)} disabled={!pickedItems.length}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: pickedItems.length ? 'pointer' : 'not-allowed', opacity: pickedItems.length ? 1 : 0.5 }}>
          <i className="ti ti-mail" style={{ fontSize: 16 }} />{t('tx_gen_email')}
        </button>
        <button onClick={() => setComposePack(true)} disabled={!pickedItems.length}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 10, border: '1.5px solid var(--accent)', background: 'transparent', color: 'var(--accent)', fontWeight: 800, fontSize: 13.5, marginLeft: 10, cursor: pickedItems.length ? 'pointer' : 'not-allowed', opacity: pickedItems.length ? 1 : 0.5 }}>
          <i className="ti ti-file-certificate" style={{ fontSize: 16 }} />Filing-complete pack
        </button>
      </div>

      {compose && <NoticeComposer clientId={clientId} year={year} month={month} items={pickedItems} sel={noticeSel} onClose={() => setCompose(false)} />}
      {composePack && <NoticeComposer mode="pack" clientId={clientId} year={year} month={month} items={pickedItems} onClose={() => setComposePack(false)} />}
    </div>
  );
}
